import { useState, useEffect } from "react"
import { User, Message, Reaction, MessageType, SendMessageRequest, ChatReaction } from '@/types'
import { useAuth } from "@/contexts/AuthContext"
import { useToast } from "@/hooks"
import { useSocket } from "@/contexts/SocketContext"
import { messageService } from "@/api"
import { useUsers } from "@/contexts/UserContext"

// Utility to deduplicate reactions: only one per userId+emoji, ignore invalid userIds
function deduplicateReactions(reactions: { userId: number | string | null, emoji: string }[]): { userId: number, emoji: string }[] {
  const seen = new Set()
  return reactions
    .map(r => ({
      userId: typeof r.userId === "string" ? Number(r.userId) : r.userId,
      emoji: r.emoji
    }))
    .filter(r => {
      if (!r.userId || typeof r.userId !== "number" || isNaN(r.userId)) return false
      const key = `${r.userId}-${r.emoji}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
}


export const useChat = () => {
  const { user } = useAuth()
  const { updateLatestMessage } = useUsers()
  const { toast } = useToast()
  const socket = useSocket()
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [showUserInfo, setShowUserInfo] = useState(false)
  const [activeChat, setActiveChat] = useState<User | null>(null)
  const [replyTo, setReplyTo] = useState<Message | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [isLoadingMessages, setIsLoadingMessages] = useState(true)

  useEffect(() => {
    const loadMessages = async () => {
      if (!user || !activeChat) return

      try {
        const response = await messageService.getMessages(String(user.id), String(activeChat.id))
        if (response.success && response.data) {
          setMessages(response.data)
        }
        setIsLoadingMessages(false)
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to load messages",
          variant: "destructive"
        })
      }
    }
    loadMessages()
  }, [activeChat, user, toast])

  useEffect(() => {
    if (!socket) return
    const handleMessage = (message: Message) => {
      setMessages(prevMessages => {
        if (message.sender.id === user?.id) {
          return prevMessages.map(m =>
            m.content === message.content &&
              m.sender.id === message.sender.id &&
              m.receiver.id === message.receiver.id
              ? { ...m, id: message.id }
              : m
          )
        }
        const messageExists = prevMessages.some(m => m.id === message.id)
        if (messageExists) return prevMessages
        return [...prevMessages, message]
      })
      if (message.sender.id === user?.id) return
      if (!activeChat || ((message.sender.id !== activeChat.id)) && message.sender.id !== user?.id) {
        if (window.Notification && Notification.permission === 'granted') {
          new Notification(message.sender.name || 'New Message ', {
            body: message.type === MessageType.TEXT  ? message.content : 'Sent file',
            icon: message.sender.avatar || '/logo.svg',
            tag: `chat-from-${message.sender.id}`,
          })
        } else if (window.Notification && Notification.permission === 'default') {
          // Automatically request permission for first-time users
          Notification.requestPermission().then(permission => {
            if (permission === 'granted') {
              new Notification(message.sender.name || 'New Message ', {
                body: message.type === MessageType.TEXT  ? message.content : 'Sent file',
                icon: message.sender.avatar || '/logo.svg',
                tag: `chat-from-${message.sender.id}`,
              })
            }
          })
        }
      } else {
        toast({
          title: `New message from ${message.sender.name}`,
          description: message.content,

        })
      }
    }

    const handleMessageEdition = (message: Message) => {
      setMessages(prevMessages =>
        prevMessages.map((m) => (
          m.id === message.id ? { ...m, content: message.content, edited: true } : m
        ))
      )
      if (message.sender.id === user?.id) return
      if (!activeChat || ((message.sender.id !== activeChat.id)) && message.sender.id !== user?.id) {
        if (window.Notification && Notification.permission === 'granted') {
          new Notification(message.sender.name + 'Edited message ', {
            body: message.type === MessageType.TEXT ? message.content : 'Sent file',
            icon: message.sender.avatar || '/logo.svg',
            tag: `chat-from-${message.sender.id}`,
          })
        } else if (window.Notification && Notification.permission === 'default') {
          // Automatically request permission for first-time users
          Notification.requestPermission().then(permission => {
            if (permission === 'granted') {
              new Notification(message.sender.name + 'Edited message ', {
                body: message.type === MessageType.TEXT ? message.content : 'Sent file',
                icon: message.sender.avatar || '/logo.svg',
                tag: `chat-from-${message.sender.id}`,
              })
            }
          })
        }
      } else {
        toast({
          title: `${message.sender.name} edited message`,
          description: message.type === MessageType.TEXT ? message.content : 'Sent file',
        })
      }
    }

    const handleMessageDeletion = (id: number) => {
      setMessages(prevMessages => prevMessages.filter((m) => m.id !== id)

      )
    }

    const handleReaction = (message: Message) => {
      console.log("Received reaction:", message)
      // Deduplicate reactions before updating state
      const cleanedReactions = deduplicateReactions(message.reactions || [])
      setMessages(prevMessages =>
        prevMessages.map(m =>
          m.id === message.id
            ? { ...m, reactions: cleanedReactions }
            : m
        )
      )
    }

    socket.onMessage(handleMessage)
    socket.onReaction(handleReaction)
    socket.onMessageEdition(handleMessageEdition)
    socket.onMessageDeletion(handleMessageDeletion)

    return () => {
      socket.removeMessageListener(handleMessage)
      socket.removeReactionListener(handleReaction)
      socket.removeMessageEditionListener(handleMessageEdition)
      socket.removeMessageDeletionListener(handleMessageDeletion)
    }
  }, [user, socket, activeChat, toast])

  const handleSendMessage = (content: string, type: MessageType, fileName: string | null, replyData?: Message) => {
    if (!user || !activeChat) {
      toast({
        title: "Error",
        description: "Please select a chat first",
        variant: "destructive"
      })
      return
    }


    const newMessage: Message = {
      id: Date.now(),
      sender: user,
      content: content,
      timestamp: new Date().toISOString(),
      receiver: activeChat,
      type,
      replyTo,
      reactions: [],
      fileName: fileName
    }
    updateLatestMessage(newMessage)

    const payload: SendMessageRequest = {
      content: newMessage.content,
      senderId: newMessage.sender.id,
      receiverId: newMessage.receiver.id,
      type: newMessage.type,
    }

    setMessages(prevMessages => [...prevMessages, newMessage])

    if (socket) {
      if (replyData) socket.messageReply(payload)
      else socket.sendMessage(payload)
    }
    setReplyTo(null)
  }

  const handleUserClick = (clickedUser: User) => {
    setActiveChat(clickedUser)
    setShowUserInfo(false)
    setReplyTo(null)
    
  }

  const handleUserAvatarClick = (clickedUser: User) => {
    setSelectedUser(clickedUser)
    setShowUserInfo(true)
  }

  const handleCloseUserInfo = () => {
    setShowUserInfo(false)
  }

  const handleReplyMessage = (message: Message) => {
    setReplyTo(message)
  }

  const handleCancelReply = () => {
    setReplyTo(null)
  }

  const handleReactToMessage = (messageId: number, reaction: Reaction) => {
    setMessages(prevMessages => {
      let hasExistingReaction = false
      const updatedMessages = prevMessages.map(message => {
        if (message.id === messageId) {
          const reactions = message.reactions || []
          const existingReaction = reactions.find(r => r.emoji === reaction.emoji && r.userId === reaction.userId)

          let updatedReactions: Reaction[]
          if (existingReaction) {
            updatedReactions = reactions.filter(r => !(r.emoji === reaction.emoji && r.userId === reaction.userId))
            hasExistingReaction = true
          } else {
            updatedReactions = [...reactions, { userId: reaction.userId, emoji: reaction.emoji }]
            hasExistingReaction = false
          }

          const updatedMessage = { ...message, reactions: updatedReactions }
          const payload: ChatReaction = { messageId, reactions: updatedReactions }
          if (socket) socket.messageReact(payload)
          return updatedMessage
        }
        return message
      })
      return updatedMessages
    })
  }

  const filteredMessages = activeChat
    ? messages.filter(message =>
      (message.sender.id === user?.id && message.receiver.id === activeChat.id) ||
      (message.sender.id === activeChat.id && message.receiver.id === user?.id)
    )
    : []

  return {
    selectedUser,
    showUserInfo,
    activeChat,
    replyTo,
    messages,
    isLoadingMessages,
    filteredMessages,
    handleSendMessage,
    handleUserClick,
    handleUserAvatarClick,
    handleCloseUserInfo,
    handleReplyMessage,
    handleCancelReply,
    handleReactToMessage,
    setMessages
  }
}
